import { ChevronDown, ChevronUp, X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * ContainerBlock — `if` / `switch` container on the pipeline canvas.
 *
 * Renders the header (rotated diamond + uppercase keyword + expression)
 * and any number of branches indented under a dashed leader.
 *
 * Editor affordances: when `onRemove` / `onMoveUp` / `onMoveDown` are
 * provided, a hover action group (matching the inline block tile) renders
 * in the header. These are wired ONLY for a top-level stage container —
 * nested switch/condition children carry no actions (container-internal
 * mutation is out of scope). At rest the header matches the static mockup;
 * the actions only fade in on hover/focus.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.container-block`.
 */
export interface ContainerBranch {
  /** Branch label shown above the indented children. */
  label: string
  /**
   * Optional inline suffix rendered after the label. Use for a styled
   * indicator such as "NEW (recipe)" — the caller controls colour + weight
   * + size via the passed ReactNode. When omitted, only `label` is shown.
   */
  labelSuffix?: React.ReactNode
  /** Children rendered inside the branch (typically pipeline blocks). */
  children: React.ReactNode
}

export interface ContainerBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  /** `condition` -> teal `IF`; `switch` -> purple `SWITCH`. */
  type: 'condition' | 'switch'
  /** Expression rendered to the right of the keyword. Monospace. */
  expression: string
  /** Branches rendered top-to-bottom under the header. */
  branches: ContainerBranch[]
  /** Remove handler — when provided, renders a hover `x` button in the header. */
  onRemove?: () => void
  /** Move-up handler — when provided, renders a hover `↑` button. */
  onMoveUp?: () => void
  /** Move-down handler — when provided, renders a hover `↓` button. */
  onMoveDown?: () => void
}

/** Visual constants shared by both variants. */
const KEYWORD_BY_TYPE = {
  condition: 'if',
  switch: 'switch',
} as const

export const ContainerBlock = React.forwardRef<HTMLDivElement, ContainerBlockProps>(
  function ContainerBlock(
    { className, type, expression, branches, onRemove, onMoveUp, onMoveDown, style, ...rest },
    ref,
  ) {
    const hasActions = Boolean(onRemove || onMoveUp || onMoveDown)
    const accent = type === 'switch' ? 'var(--pe-accent-switch)' : 'var(--pe-accent-conditional)'
    const tint = type === 'switch' ? 'rgba(168, 85, 247, 0.04)' : 'rgba(20, 184, 166, 0.04)'
    const borderTint = type === 'switch' ? 'rgba(168, 85, 247, 0.25)' : 'rgba(20, 184, 166, 0.25)'
    const branchBorderTint =
      type === 'switch' ? 'rgba(168, 85, 247, 0.4)' : 'rgba(20, 184, 166, 0.4)'

    return (
      <div
        ref={ref}
        className={cn('group/container rounded-[6px] px-2.5 pt-2 pb-1.5', className)}
        style={{
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: borderTint,
          backgroundImage: `linear-gradient(180deg, ${tint} 0%, transparent 100%)`,
          ...style,
        }}
        data-testid="pipeline-container-block"
        data-type={type}
        {...rest}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <span
            aria-hidden
            className="w-3 h-3 shrink-0 rotate-45 rounded-[2px]"
            style={{
              background: accent,
              boxShadow: `0 0 6px ${accent}66`,
            }}
          />
          <span
            className="shrink-0 font-mono text-[9.5px] uppercase tracking-[0.10em]"
            style={{ color: accent }}
          >
            {KEYWORD_BY_TYPE[type]}
          </span>
          <span
            className="flex-1 min-w-0 truncate font-mono text-[10.5px]"
            style={{ color: 'var(--pe-text-secondary)' }}
          >
            {expression}
          </span>
          {hasActions && (
            <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover/container:opacity-100 focus-within:opacity-100 transition-opacity">
              {onMoveUp && (
                <button
                  type="button"
                  onClick={onMoveUp}
                  aria-label={`Move ${KEYWORD_BY_TYPE[type]} block up`}
                  data-testid="pipeline-block-move-up"
                  className="w-4 h-4 flex items-center justify-center rounded-[3px] hover:bg-ia-elevated text-ia-text-tertiary hover:text-ia-text-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <ChevronUp size={11} strokeWidth={2} />
                </button>
              )}
              {onMoveDown && (
                <button
                  type="button"
                  onClick={onMoveDown}
                  aria-label={`Move ${KEYWORD_BY_TYPE[type]} block down`}
                  data-testid="pipeline-block-move-down"
                  className="w-4 h-4 flex items-center justify-center rounded-[3px] hover:bg-ia-elevated text-ia-text-tertiary hover:text-ia-text-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <ChevronDown size={11} strokeWidth={2} />
                </button>
              )}
              {onRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  aria-label={`Remove ${KEYWORD_BY_TYPE[type]} block`}
                  data-testid="pipeline-block-remove"
                  className="w-4 h-4 flex items-center justify-center rounded-[3px] hover:bg-ia-elevated text-ia-text-tertiary hover:text-ia-error focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <X size={11} strokeWidth={2} />
                </button>
              )}
            </div>
          )}
        </div>

        {branches.map((branch, idx) => (
          <div
            key={`${branch.label}-${idx}`}
            className="ml-[5px] pl-2.5 py-1 flex flex-col gap-1.5"
            style={{
              borderLeft: '1px dashed',
              borderLeftColor: branchBorderTint,
            }}
            data-testid="pipeline-container-branch"
            data-branch-label={branch.label}
          >
            <span
              className="inline-flex items-center gap-1 font-mono text-[8.5px] uppercase tracking-[0.12em] mb-0.5"
              style={{ color: accent }}
            >
              <span style={{ color: 'var(--pe-text-muted)' }} aria-hidden>
                {/* ASCII corner box-drawing analogue to the mockup's &boxur; */}
                {String.fromCharCode(0x2514)}
              </span>
              {branch.label}
              {branch.labelSuffix}
            </span>
            {branch.children}
          </div>
        ))}
      </div>
    )
  },
)
